import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Building2, Shield, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useBalance } from '../hooks/useBalance';
import { useAuth } from '../context/useAuth';
import { formatCredits, shouldShowBalance } from '../utils/credits';

export function UserProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { balance } = useBalance(true);
  const { user } = useAuth();


  useEffect(() => {
    async function fetchProfile() {
      try {
        const r = await axios.get('/user/me');
        setProfile(r.data);
      } catch (err) {
        setError('Failed to load profile');
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  const creditText = shouldShowBalance(user?.tier) ? formatCredits(balance?.availableCredits) : '';
  const showCredits = creditText !== '';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>

        {/* Profile info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User size={20} className="text-blue-600" />
            Profile
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Mail size={16} />
                Email
              </div>
              <span className="text-sm font-medium text-gray-900">{profile?.email || '—'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User size={16} />
                Name
              </div>
              <span className="text-sm font-medium text-gray-900">{profile?.full_name || 'Not set'}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Building2 size={16} />
                Organisation
              </div>
              <span className="text-sm font-medium text-gray-900">{profile?.organisation_id?.slice(0, 8) || '—'}...</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CreditCard size={16} />
                Plan
              </div>
              <span className="text-sm font-medium text-gray-900">{user?.tier || '—'}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Shield size={16} />
                Sign-in method
              </div>
              <span className="text-sm font-medium text-gray-900">
                Email link
              </span>
            </div>
          </div>
        </div>

        {/* Billing summary — credits visible for PAYG/TEAM only */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard size={20} className="text-blue-600" />
            Billing
          </h2>
          {showCredits && (
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-500">Credit Balance</span>
              <span className="text-sm font-medium text-gray-900">
                {creditText}
              </span>
            </div>
          )}
          <div className="mt-4 flex items-center gap-4">
            <Link
              to="/billing"
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Top up credits &rarr;
            </Link>
            <Link
              to="/org/billing"
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Usage dashboard
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
